import type { CSSProperties, ReactNode } from 'react';
import type { formatDateParts } from '../utils/utils';
import { DateTimeLayout } from './date-time-layout';

export type DateTimeParts = ReturnType<typeof formatDateParts>;

// each layout's own spacing/shape, scaled up from the Figma layout thumbnails to the email's size -
// any template preset or user style for these still wins
const LAYOUT_DEFAULTS: Record<
  DateTimeLayout,
  { cellPadding: string; borderRadius: string; borderStyle: CSSProperties['borderStyle'] }
> = {
  [DateTimeLayout.Rows]: { cellPadding: '20px', borderRadius: '12px', borderStyle: 'solid' },
  [DateTimeLayout.Cards]: { cellPadding: '20px', borderRadius: '16px', borderStyle: 'solid' },
  [DateTimeLayout.Stacked]: { cellPadding: '48px', borderRadius: '6px', borderStyle: 'solid' },
  [DateTimeLayout.Compact]: { cellPadding: '16px 24px', borderRadius: '12px', borderStyle: 'none' },
};

export interface DateTimeCardWidgetProps {
  blockId?: string; // lets the editor find this rendered node in the DOM
  layout?: DateTimeLayout;
  dateLabel?: string | null;
  timeLabel?: string | null;
  /** the session start split into weekday/date/period/time - only the compact layout needs it */
  dateParts?: DateTimeParts | null;
  accentColor?: string;
  headingColor?: string;
  bodyFont?: string;
  headingFont?: string;
  borderColor?: string;
  /** card border width - e.g. '1px' */
  border?: string;
  borderStyle?: CSSProperties['borderStyle'];
  dividerColor?: string;
  /** outer wrapper background - spans the full width including the padding gutters */
  background?: string;
  /** inner bordered box's own background, for templates that color just the card (not its gutters) */
  cardBackground?: string;
  /** inner bordered box's corner radius - some templates (e.g. amalfi) have square corners */
  cardBorderRadius?: string;
  /** "Date"/"Time" label font-weight - differs slightly per template's original design */
  labelFontWeight?: number;
  padding?: string;
  /** Date row's own cell padding, independent of the outer wrapper padding - the other layouts pad
   * every cell with this */
  dateRowPadding?: string;
  /** Time row's own cell padding - independently overridable since some templates (e.g.
   * montclair, which has no divider between rows) zero its top padding to avoid a double gap */
  timeRowPadding?: string;
  /** a template's own label/value type, merged over the layout's - lets a template keep its look */
  labelStyle?: CSSProperties;
  valueStyle?: CSSProperties;
  /** label text overrides - e.g. florence's "/ Date" */
  dateHeading?: string;
  timeHeading?: string;
  /** stacked: space between the date and time groups */
  groupGap?: string;
  /** stacked: a fixed-size card, left-aligned (viceroy) instead of full width */
  cardWidth?: number;
  cardHeight?: number;
  /** compact: the full weekday ("MONDAY") rather than the short one ("MON") */
  longDayName?: boolean;
}

const presentationTable = { role: 'presentation', cellPadding: 0, cellSpacing: 0, border: 0 } as const;

export default function DateTimeCardWidget({
  blockId,
  layout = DateTimeLayout.Rows,
  dateLabel,
  timeLabel,
  dateParts,
  accentColor = 'var(--accent)',
  headingColor = 'var(--heading)',
  bodyFont = 'var(--body-font)',
  headingFont = 'var(--heading-font)',
  borderColor = 'var(--accent)',
  border = '1px',
  borderStyle,
  dividerColor = 'rgba(255,255,255,0.1)',
  background,
  cardBackground,
  cardBorderRadius,
  labelFontWeight = 600,
  padding = '0 56px',
  dateRowPadding,
  timeRowPadding,
  labelStyle: labelOverrides,
  valueStyle: valueOverrides,
  dateHeading = 'Date',
  timeHeading,
  groupGap = '36px',
  cardWidth,
  cardHeight,
  longDayName = false,
}: DateTimeCardWidgetProps) {
  if (!dateLabel && !timeLabel) return null;

  const defaults = LAYOUT_DEFAULTS[layout] ?? LAYOUT_DEFAULTS[DateTimeLayout.Rows];
  const resolvedBorderStyle = borderStyle ?? defaults.borderStyle;
  const cardStyle: CSSProperties = {
    border: resolvedBorderStyle === 'none' ? 'none' : `${border} ${resolvedBorderStyle} ${borderColor}`,
    borderRadius: cardBorderRadius ?? defaults.borderRadius,
    background: cardBackground,
  };
  const cellPadding = dateRowPadding ?? defaults.cellPadding;

  let content: ReactNode;
  switch (layout) {
    case DateTimeLayout.Cards: {
      const labelStyle: CSSProperties = {
        color: accentColor,
        fontSize: '12px',
        fontFamily: bodyFont,
        fontWeight: labelFontWeight,
        textTransform: 'uppercase',
        ...labelOverrides,
      };
      const valueStyle: CSSProperties = {
        color: headingColor,
        fontSize: '16px',
        fontFamily: bodyFont,
        fontWeight: 700,
        paddingTop: '8px',
        ...valueOverrides,
      };
      const card = (label: string, value: string, isFirst: boolean) => (
        // the first card carries the block id, so the style panel reads the cards' shared box style
        <td
          data-block-id={isFirst ? blockId : undefined}
          data-layout={isFirst ? layout : undefined}
          width="50%"
          valign="top"
          style={{ ...cardStyle, padding: cellPadding }}
        >
          <table {...presentationTable}>
            <tbody>
              <tr>
                <td valign="middle" style={{ paddingRight: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: accentColor }} />
                </td>
                <td style={labelStyle}>{label}</td>
              </tr>
            </tbody>
          </table>
          <div style={valueStyle}>{value}</div>
        </td>
      );
      content = (
        <table width="100%" {...presentationTable}>
          <tbody>
            <tr>
              {dateLabel && card(dateHeading, dateLabel, true)}
              {dateLabel && timeLabel && <td style={{ width: '24px', minWidth: '24px' }}>&nbsp;</td>}
              {timeLabel && card(timeHeading ?? 'Time', timeLabel, !dateLabel)}
            </tr>
          </tbody>
        </table>
      );
      break;
    }
    case DateTimeLayout.Stacked: {
      const labelStyle: CSSProperties = {
        color: accentColor,
        fontSize: '18px',
        fontFamily: bodyFont,
        fontWeight: labelFontWeight,
        textTransform: 'uppercase',
        marginBottom: '6px',
        ...labelOverrides,
      };
      const valueStyle: CSSProperties = {
        color: headingColor,
        fontSize: '30px',
        fontFamily: bodyFont,
        fontWeight: 700,
        ...valueOverrides,
      };
      content = (
        <table
          data-block-id={blockId}
          data-layout={layout}
          width={cardWidth ?? '100%'}
          {...presentationTable}
          style={cardStyle}
        >
          <tbody>
            <tr>
              <td data-block-padding height={cardHeight} valign="top" style={{ padding: cellPadding }}>
                {dateLabel && (
                  <>
                    <div style={labelStyle}>{dateHeading}</div>
                    <div style={valueStyle}>{dateLabel}</div>
                  </>
                )}
                {timeLabel && (
                  <div style={{ paddingTop: dateLabel ? groupGap : undefined }}>
                    <div style={labelStyle}>{timeHeading ?? 'Start time'}</div>
                    <div style={valueStyle}>{timeLabel}</div>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      );
      break;
    }
    case DateTimeLayout.Compact: {
      if (!dateParts) return null;
      const smallStyle: CSSProperties = {
        color: headingColor,
        fontSize: '14px',
        fontFamily: bodyFont,
        fontWeight: 500,
        letterSpacing: '0.42px',
        textTransform: 'uppercase',
        textAlign: 'center',
        ...labelOverrides,
      };
      const largeStyle: CSSProperties = {
        color: headingColor,
        fontSize: '32px',
        fontFamily: headingFont,
        textAlign: 'center',
        ...valueOverrides,
      };
      content = (
        <table
          data-block-id={blockId}
          data-layout={layout}
          align="center"
          {...presentationTable}
          style={{ ...cardStyle, margin: '0 auto' }}
        >
          <tbody>
            <tr>
              <td data-block-padding align="center" style={{ padding: cellPadding }}>
                <div style={smallStyle}>{longDayName ? dateParts.dayName : dateParts.shortDayName}</div>
                <div style={largeStyle}>{dateParts.shortDate}</div>
              </td>
              <td valign="middle">
                <div style={{ width: '38px', borderTop: `1px solid ${headingColor}`, fontSize: 0, lineHeight: '0' }}>
                  &nbsp;
                </div>
              </td>
              <td align="center" style={{ padding: cellPadding }}>
                <div style={smallStyle}>{dateParts.period}</div>
                <div style={largeStyle}>{dateParts.time}</div>
              </td>
            </tr>
          </tbody>
        </table>
      );
      break;
    }
    default: {
      const rowStyle: CSSProperties = {
        color: accentColor,
        fontSize: '14px',
        fontFamily: bodyFont,
        fontWeight: labelFontWeight,
        ...labelOverrides,
      };
      const valueStyle: CSSProperties = {
        color: headingColor,
        fontSize: '14px',
        fontFamily: bodyFont,
        fontWeight: 400,
        ...valueOverrides,
      };
      content = (
        <table data-block-id={blockId} data-layout={layout} width="100%" {...presentationTable} style={cardStyle}>
          <tbody>
            {dateLabel && (
              <tr>
                <td data-block-padding style={{ padding: cellPadding, borderBottom: `1px solid ${dividerColor}` }}>
                  <table width="100%" {...presentationTable}>
                    <tbody>
                      <tr>
                        <td style={rowStyle}>{dateHeading}</td>
                        <td align="right" style={valueStyle}>
                          {dateLabel}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
            {timeLabel && (
              <tr>
                <td data-block-padding style={{ padding: timeRowPadding ?? defaults.cellPadding }}>
                  <table width="100%" {...presentationTable}>
                    <tbody>
                      <tr>
                        <td style={rowStyle}>{timeHeading ?? 'Time'}</td>
                        <td align="right" style={valueStyle}>
                          {timeLabel}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    }
  }

  return (
    <table width="100%" {...presentationTable} style={{ background }}>
      <tbody>
        <tr>
          <td style={{ padding }}>{content}</td>
        </tr>
      </tbody>
    </table>
  );
}
